import { request } from "utils/request"
import { asyncAction } from "./common"
import { DateTime } from "luxon"
import { analyses, message } from "./"
import i18n from "i18n"
import { getState } from "storeAccess"

const initialState = {
  isLoading: false,
  needsRefresh: false,
  sampleGroups: null,
  sampleGroupsSortedByProduct: null,
  shouldBeDoneSamples: null,
  productAnalysis: null,
}

const updateFlagStatus = (groups, sampleName, analysisId, isFlagged, isSorted) => {
  if (isSorted) {
    return groups.map((product) => {
      const key = Object.keys(product)[0];
      return {
        ...product,
        [key]: product[key].map((sample) => {
          if (sample.sampleName === sampleName) {
            return {
              ...sample,
              analyses: sample.analyses.map((analysis) => {
                if (analysis.sampleId === analysisId) {
                  return {
                    ...analysis,
                    isFlagged,
                  };
                }
                return analysis;
              }),
            };
          }
          return sample;
        }),
      };
    });
  } else {
    return groups.map((sample) => {
      if (sample.sampleName === sampleName) {
        return {
          ...sample,
          analyses: sample.analyses.map((analysis) => {
            if (analysis.sampleId === analysisId) {
              return {
                ...analysis,
                isFlagged,
              };
            }
            return analysis;
          }),
        };
      }
      return sample;
    });
  }
};



export function reducer(state = initialState, action) {
  switch (action.type) {
    case "sampleView/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "sampleView/fetch":
      const normalizedSampleItems = (action.res.items || []).map((sample) => {
        const sampleCustomerName = sample.customerName ?? sample.customer_name ?? null
        return {
          ...sample,
          customerName: sampleCustomerName,
          analyses: (sample.analyses || []).map((analysis) => ({
            ...analysis,
            customerName: analysis.customerName ?? analysis.customer_name ?? sampleCustomerName,
          })),
        }
      })

      const uniqueNames = {}
      normalizedSampleItems.forEach((obj) => {
        const productName = obj.productName === "" ? null : obj.productName
        if (!uniqueNames[productName]) {
          uniqueNames[productName] = {
            [productName]: [],
          }
        }
        uniqueNames[productName][productName].push({ ...obj, productName })
      })

      // sortedByProduct uses a reference of values from action.res
      // this causes both to update when we change a sample priority
      const sortedByProduct = Object.values(uniqueNames)

      sortedByProduct.sort((a, b) => {
        const keyA = Object.keys(a)[0];
        const keyB = Object.keys(b)[0];
        // the empty string check is to make sure that empty product names are always at the last index of the sorted array
        const isKeyAEmpty = keyA === '' || keyA === null || keyA === 'null';
        const isKeyBEmpty = keyB === '' || keyB === null || keyB === 'null';

        if (isKeyAEmpty && isKeyBEmpty) {
          return 0;
        }
        if (isKeyAEmpty) {
          return 1;
        }
        if (isKeyBEmpty) {
          return -1;
        }
        if (keyA < keyB) {
          return -1;
        }
        if (keyA > keyB) {
          return 1;
        }
        return 0;
      });
      return { ...state, isLoading: false, needsRefresh: false, sampleGroups: normalizedSampleItems, sampleGroupsSortedByProduct: sortedByProduct, shouldBeDoneSamples: action.res.should_be_done_samples, productAnalysis: action.res.product_analysis_relationships}
    case "auth/logout":
      return initialState
    case "analyses/moveIntoPlan":
    case "analyses/remove":
      return { ...state, needsRefresh: true }
    case "analyses/removeAllPlanned":
    case "samples/unplan-samples":
    case "analysisTasks/move":
    case "analysisTasks/edit":
    case "iap/run":
    case "iap/runFillInSamples":
    case "samples/assign-samples":
      return {...initialState, needsRefresh: true}
    case "samples/changePriority":
      // Update maxPriority for the specified sample in both sampleGroups and sampleGroupsSortedByProduct
      const [newPriority, targetSampleName, targetObservationId] = action.args
      const updatedSampleGroups = (state.sampleGroups || []).map((sample) =>
        sample.sampleName === targetSampleName && String(sample.observationId) === String(targetObservationId) ? { ...sample, maxPriority: newPriority } : sample
      )

      const updatedSampleGroupsSortedByProduct = (state.sampleGroupsSortedByProduct || []).map((product) => {
        const key = Object.keys(product)[0]
        const items = product[key] || []
        return { [key]: items.map((sample) => (sample.sampleName === targetSampleName && String(sample.observationId) === String(targetObservationId) ? { ...sample, maxPriority: newPriority } : sample)) }
      })

      return { ...state, sampleGroups: updatedSampleGroups, sampleGroupsSortedByProduct: updatedSampleGroupsSortedByProduct }
    case "samples/changePriority/OPTIMISTIC":
      // Optimistic update: same as changePriority but triggered synchronously
      {
        const [optPriority, optSampleName, optObservationId] = action.args
        const updatedP = (state.sampleGroups || []).map((sample) =>
          sample.sampleName === optSampleName && String(sample.observationId) === String(optObservationId) ? { ...sample, maxPriority: optPriority } : sample
        )
        const updatedSortedP = (state.sampleGroupsSortedByProduct || []).map((product) => {
          const key = Object.keys(product)[0]
          const items = product[key] || []
          return { [key]: items.map((sample) => (sample.sampleName === optSampleName && String(sample.observationId) === String(optObservationId) ? { ...sample, maxPriority: optPriority } : sample)) }
        })
        return { ...state, sampleGroups: updatedP, sampleGroupsSortedByProduct: updatedSortedP }
      }
    case "samples/changePriority/REVERT":
      // Revert optimistic update when the API fails
      {
        const [oldPriority, revertSampleName, revertObservationId] = action.args
        const reverted = (state.sampleGroups || []).map((sample) =>
          sample.sampleName === revertSampleName && String(sample.observationId) === String(revertObservationId) ? { ...sample, maxPriority: oldPriority } : sample
        )
        const revertedSorted = (state.sampleGroupsSortedByProduct || []).map((product) => {
          const key = Object.keys(product)[0]
          const items = product[key] || []
          return { [key]: items.map((sample) => (sample.sampleName === revertSampleName && String(sample.observationId) === String(revertObservationId) ? { ...sample, maxPriority: oldPriority } : sample)) }
        })
        return { ...state, sampleGroups: reverted, sampleGroupsSortedByProduct: revertedSorted }
      }
    case "samples/flag-sample": {
      const { sampleName, analysisId } = action.payload || action.args;

      return {
        ...state,
        sampleGroups: updateFlagStatus(state.sampleGroups, sampleName, analysisId, true, false),
        sampleGroupsSortedByProduct: updateFlagStatus(
          state.sampleGroupsSortedByProduct,
          sampleName,
          analysisId,
          true,
          true
        ),
      };
    }

    case "samples/unflag-sample": {
      const { sampleName, analysisId } = action.payload || action.args;

      return {
        ...state,
        sampleGroups: updateFlagStatus(state.sampleGroups, sampleName, analysisId, false, false),
        sampleGroupsSortedByProduct: updateFlagStatus(
          state.sampleGroupsSortedByProduct,
          sampleName,
          analysisId,
          false,
          true
        ),
      };
    }
    case "samples/fail-samples":
      const [analysisId, , isSampleView, sampleName, productName, observationId] = action.args
      if(!isSampleView) return {...initialState, needsRefresh: true}
      const updatedName = productName || ''
      return {
        ...state,
        sampleGroups: state.sampleGroups.map((sample) => {
          if (sample.sampleName === sampleName && String(sample.observationId) === String(observationId)) {
            return {
              ...sample,
              isDone: false,
              analyses: sample.analyses.map((sampleAnalysis) => {
                if (sampleAnalysis.analysisId === analysisId) {
                  return {
                    ...sampleAnalysis,
                    analysesPlanned: false,
                    tasksStopped: false,
                  }
                }
                return sampleAnalysis
              }),
            }
          }
          return sample
        }),
        sampleGroupsSortedByProduct: state.sampleGroupsSortedByProduct.map((product) => {
          if (Object.keys(product)[0] === updatedName) {
            return {
              ...product,
              [updatedName]: product[updatedName].map((sample) => {
                if (sample.sampleName === sampleName && String(sample.observationId) === String(observationId)) {
                  return {
                    ...sample,
                    isDone: false,
                    analyses: sample.analyses.map((sampleAnalysis) => {
                      if (sampleAnalysis.analysisId === analysisId) {
                        return {
                          ...sampleAnalysis,
                          analysesPlanned: false,
                          tasksStopped: false,
                        }
                      }
                      return sampleAnalysis
                    }),
                  }
                }
                return sample
              }),
            }
          }
          return product
        }),
      }
    case "sampleView/deleteSample": {
      // Remove any analyses that reference the deleted sampleId from both representations
      const deletedSampleId = action.res && action.res.sampleId ? action.res.sampleId : (action.args && action.args[0])
      if (!deletedSampleId) return state

      const filterOutSampleId = (groups, isSorted) => {
        if (!groups) return groups
        
        // If sortedByProduct (array of { productName: [samples] })
        if (isSorted) {
          return groups
            .map((product) => {
              const key = Object.keys(product)[0]
              const items = product[key] || []
              const newItems = items
                .map((sample) => {
                  const newAnalyses = (sample.analyses || []).filter((a) => String(a.sampleId) !== String(deletedSampleId))
                  if (newAnalyses.length === 0) return null
                  return { ...sample, analyses: newAnalyses }
                })
                .filter(Boolean)
              if (newItems.length === 0) return null
              return { [key]: newItems }
            })
            .filter(Boolean)
        }

        // Default: sampleGroups is an array of samples
        return groups
          .map((sample) => {
            const newAnalyses = (sample.analyses || []).filter((a) => String(a.sampleId) !== String(deletedSampleId))
            if (newAnalyses.length === 0) return null
            return { ...sample, analyses: newAnalyses }
          })
          .filter(Boolean)
      }

      return {
        ...state,
        sampleGroups: filterOutSampleId(state.sampleGroups, false),
        sampleGroupsSortedByProduct: filterOutSampleId(state.sampleGroupsSortedByProduct, true),
      }
    }

    case "sampleView/removeProductAnalysis": {
      const removedId = action.res.id
      const updatedProductAnalysis = { ...state.productAnalysis }

      // Iterate through each product and remove the relationship with matching id
      Object.keys(updatedProductAnalysis).forEach((productName) => {
        if (Array.isArray(updatedProductAnalysis[productName])) {
          updatedProductAnalysis[productName] = updatedProductAnalysis[productName].filter(
            (relationship) => relationship.id !== removedId
          )
        }
      })

      return { ...state, productAnalysis: updatedProductAnalysis }
    }

    default:
      return state
  }
}

export const fetch = asyncAction("sampleView/fetch", (dispatch) => {
  // Fetch from today plus two days
  const after = DateTime.local().set({hour: 12}).plus({ days: 2 }).toUTC().toISO()
  return request("GET", `/analyses/sample-view?after=${after}`).then((res) => {
    return res
  })
})

// Delete a single unplanned sample by id and update sample view
export const deleteSample = asyncAction("sampleView/deleteSample", (dispatch, sampleId) => {
  return request("DELETE", `/sample/${sampleId}`).then((res) => {
    try {
      dispatch(analyses.fetchUnplanned())
    } catch (e) {}
    try {
      dispatch(message.success(i18n.t("adhoc.sampleDeleted", { ns: "messages" })))
    } catch (e) {}
    // Return sampleId for reducer to handle local removal
    return { sampleId }
  })
})

// Edit all manual observations within the same sample group (same sample_name)
export const editSampleGroup = asyncAction(
  "sampleView/editSampleGroup",
  (dispatch, sampleId, patchData) => {
    // PATCH /sample/<uuid:sample_id>/group
    return request("PATCH", `/sample/${sampleId}/group`, { patch: patchData }).then((res) => {
      // On success, merge returned sampleGroups/samples into sampleView state when possible
      try {
        const state = getState()
        if (res && (res.sampleGroups || res.samples)) {
          const current = state.sampleView.sampleGroups || []
          const newGroups = res.sampleGroups || []

          const merged = current.slice()
          newGroups.forEach((ng) => {
            // First try to find an existing group that contains any of the same
            // analysis/sample UUIDs (ng.analyses[].sampleId). This handles renames
            // where the sampleName changed but the underlying sample observations
            // (sampleIds) remain the same. Fallback to sampleName + observationId
            // match when sampleIds are not available.
            const newSampleIds = (ng.analyses || []).map((a) => String(a.sampleId))
            let idx = -1
            if (newSampleIds.length > 0) {
              idx = merged.findIndex((s) => (s.analyses || []).some((a) => newSampleIds.includes(String(a.sampleId))))
            }
            if (idx < 0) {
              idx = merged.findIndex((s) => s.sampleName === ng.sampleName && String(s.observationId) === String(ng.observationId))
            }
            if (idx >= 0) {
              merged[idx] = ng
            } else {
              merged.push(ng)
            }
          })

          const resForSampleView = {
            items: merged,
            should_be_done_samples: state.sampleView.shouldBeDoneSamples || [],
            product_analysis_relationships: state.sampleView.productAnalysis || {},
          }

          // Dispatch a synthetic sampleView/fetch action with merged items so reducer will rebuild sorted groups
          dispatch({ type: "sampleView/fetch", res: resForSampleView })
        } else {
          dispatch(fetch())
        }

        dispatch(analyses.fetchUnplanned())
      } catch (e) {}
      if (res && res.samples) {
        dispatch(message.success(i18n.t("adhoc.samplesUpdated", { ns: "messages" })))
      }
      return res
    })
  }
)

// Mark a single planned sample observation as DONE
export const markSampleDone = asyncAction(
  "sampleView/markSampleDone",
  (dispatch, sampleId) => {
    return request("POST", `/sample/${sampleId}/mark-done`).then((res) => {
      try {
        // If the API returned sampleGroups/samples, merge them into the current sampleView state
        // to avoid fetching the entire sample view again. Otherwise fall back to fetching.
        const state = getState()
        if (res && (res.sampleGroups || res.samples)) {
          const current = state.sampleView.sampleGroups || []
          const newGroups = res.sampleGroups || []

          const merged = current.slice()
          newGroups.forEach((ng) => {
            // First try to find an existing group that contains any of the same
            // analysis/sample UUIDs (ng.analyses[].sampleId). This handles renames
            // where the sampleName changed but the underlying sample observations
            // (sampleIds) remain the same. Fallback to sampleName + observationId
            // match when sampleIds are not available.
            const newSampleIds = (ng.analyses || []).map((a) => String(a.sampleId))
            let idx = -1
            if (newSampleIds.length > 0) {
              idx = merged.findIndex((s) => (s.analyses || []).some((a) => newSampleIds.includes(String(a.sampleId))))
            }
            if (idx < 0) {
              idx = merged.findIndex((s) => s.sampleName === ng.sampleName && String(s.observationId) === String(ng.observationId))
            }
            if (idx >= 0) {
              merged[idx] = ng
            } else {
              merged.push(ng)
            }
          })

          const resForSampleView = {
            items: merged,
            should_be_done_samples: state.sampleView.shouldBeDoneSamples || [],
            product_analysis_relationships: state.sampleView.productAnalysis || {},
          }

          // Dispatch a synthetic sampleView/fetch action with merged items so reducer will rebuild sorted groups
          dispatch({ type: "sampleView/fetch", res: resForSampleView })
        } else {
          // Fallback: fetch all data if response doesn't include sampleGroups/samples
          dispatch(fetch())
        }

        dispatch(analyses.fetchPlanned())
        dispatch(analyses.fetchUnplanned())
      } catch (e) {}
      try {
        dispatch(message.success(i18n.t("adhoc.sampleMarkedDone", { ns: "messages" })))
      } catch (e) {}
      return res
    })
  }
)

export const removeProductAnalysis = asyncAction("sampleView/removeProductAnalysis", (dispatch, id) => {
  return request("DELETE", `/product-analysis-relationship/${id}`).then(() => ({ id }))
})
